import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ReactComponent as Lobby } from '../../../../assets/img/sidebar/Lobby.svg';

import { ReactComponent as Team } from '../../../../assets/img/sidebar/Team.svg';
import { ReactComponent as Transfers } from '../../../../assets/img/sidebar/Transfers.svg';
import { ReactComponent as League } from '../../../../assets/img/sidebar/League.svg';
import { ReactComponent as Finance } from '../../../../assets/img/sidebar/Finance.svg';
import { ReactComponent as Store } from '../../../../assets/img/sidebar/Store.svg';
import { ReactComponent as DomesticCup } from '../../../../assets/img/sidebar/Cup.svg';
import { ReactComponent as CrossServer } from '../../../../assets/img/sidebar/Globe.svg';
import { ReactComponent as UserGuide } from '../../../../assets/img/sidebar/UserGuide.svg';
import { ReactComponent as Kits } from '../../../../assets/img/sidebar/kits.svg';
import {
  StyledSideNav,
  StyledSummary,
  StyledNavLinks,
  StyledTextNavLink,
  StyledMobileContainer,
  StyledAvatar,
  StyledRankingContainer,
  StyledSidebar,
  StyledNavList,
  StyledNavHighlight,
  StyledSelectContainer,
  StyledIdentityCopy,
  StyledIdentityName,
  StyledLegalRail,
  StyledAccountBlock,
} from './styled';
import UserDropdown from '../../../UserDropdown';

import { showPopupWindow } from '../../../../redux/actions/Popups';
import { allowTour } from '../../../../redux/actions/TourControl';
import { userLogout } from '../../../../redux/actions/User';
import { sidebarOpenSideNavigation } from '../../../../redux/actions/Sidebar';
import { enableTourReplay, resetAllTours } from '../../../../helpers/tour/locationToStorage';

import { dataForSidebar } from '../../../../redux/selector/Sidebar';
import { getUser, isAuth } from '../../../../redux/selector/User';

import { toOrdinalSuffix } from '../../../../service/utils';
import { POPUPS } from '../../../../service/constants';
import ClubSwitcher from '../../../ClubSwitcher';

export default React.memo(function Menu({ showHeader, isSideOpen }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navListRef = useRef(null);
  const [highlight, setHighlight] = useState({
    top: 0,
    height: 0,
    ready: false,
  });

  const user = useSelector(getUser);
  const isAuthenticated = useSelector(isAuth);
  const data = useSelector(dataForSidebar);
  const avatarUrl = `https://cdn.discordapp.com/avatars/${user.discord}/${user.avatarID}.jpg`;

  const openInvitePopup = () => {
    dispatch(showPopupWindow(POPUPS.INVITE_POPUP, POPUPS.INVITE_POPUP));
  };

  const openSettingsPopup = () => {
    dispatch(sidebarOpenSideNavigation(false));
    dispatch(showPopupWindow(POPUPS.SETTINGS_POPUP, POPUPS.SETTINGS_POPUP));
  };

  const logout = () => {
    dispatch(userLogout());
    localStorage.removeItem('isFromRegister');
  };

  const showTutorial = () => {
    enableTourReplay();
    resetAllTours();
    dispatch(sidebarOpenSideNavigation(false));
    dispatch(allowTour(true));
  };

  const leaveGame = () => {
    dispatch(showPopupWindow(POPUPS.LEAVE_POPUP, POPUPS.LEAVE_POPUP));
  };

  const updateHighlight = useCallback(() => {
    const list = navListRef.current;
    if (!list) return;
    const selected = list.querySelector('a.selected');
    if (!selected) {
      setHighlight((current) => ({ ...current, ready: false }));
      return;
    }
    setHighlight({
      top: selected.offsetTop,
      height: selected.offsetHeight,
      ready: true,
    });
  }, []);

  useLayoutEffect(() => {
    updateHighlight();
  }, [updateHighlight, location.pathname, isSideOpen]);

  useLayoutEffect(() => {
    window.addEventListener('resize', updateHighlight);
    return () => window.removeEventListener('resize', updateHighlight);
  }, [updateHighlight]);

  return (
    <StyledSideNav className="sidebar" data-sideopen={isSideOpen}>
      <StyledSummary className={showHeader ? '' : 'isOnMain'}>
        <StyledMobileContainer>
          <StyledAvatar src={avatarUrl} alt="" />
          <StyledIdentityCopy>
            <StyledIdentityName>{user.username || 'Manager'}</StyledIdentityName>
            <StyledRankingContainer to="/league">
              {Number.isFinite(data.ranking)
                ? `${toOrdinalSuffix(data.ranking + 1)} in League`
                : 'League'}
            </StyledRankingContainer>
          </StyledIdentityCopy>
        </StyledMobileContainer>
        <StyledSelectContainer>
          <ClubSwitcher variant="nav" />
        </StyledSelectContainer>
      </StyledSummary>
      <StyledSidebar>
        <StyledNavList ref={navListRef}>
          <StyledNavHighlight
            aria-hidden="true"
            $ready={highlight.ready}
            style={{
              transform: `translateY(${highlight.top}px)`,
              height: highlight.height,
            }}
          />
          <StyledNavLinks to="/home" activeClassName="selected" href="#">
            <Lobby />
            <span>Lobby</span>
          </StyledNavLinks>
          <StyledNavLinks activeClassName="selected" to="/team" href="#">
            <Team />
            <span>Team</span>
          </StyledNavLinks>
          <StyledNavLinks activeClassName="selected" to="/transfers" href="#">
            <Transfers />
            <span>Transfers</span>
          </StyledNavLinks>
          <StyledNavLinks activeClassName="selected" to="/stuff" href="#">
            <Kits />
            <span>Kits</span>
          </StyledNavLinks>
          <StyledNavLinks
            activeClassName="selected"
            to={{ pathname: 'https://the-open-league.myshopify.com/' }}
            target="_blank"
          >
            <Store />
            <span>Store</span>
          </StyledNavLinks>

          <StyledNavLinks activeClassName="selected" to="/league" href="#">
            <League />
            <span>League</span>
          </StyledNavLinks>
          <StyledNavLinks
            activeClassName="selected"
            to="/domestic-cup"
            href="#"
          >
            <DomesticCup />
            <span>Cup</span>
          </StyledNavLinks>
          <StyledNavLinks
            activeClassName="selected"
            to="/cross-server"
            href="#"
          >
            <CrossServer />
            <span>Cross server</span>
          </StyledNavLinks>
          <StyledNavLinks activeClassName="selected" to="/finance" href="#">
            <Finance />
            <span>Finance</span>
          </StyledNavLinks>

          <StyledNavLinks activeClassName="selected" to="/user-guide" href="#">
            <UserGuide />
            <span>User guide</span>
          </StyledNavLinks>
        </StyledNavList>
        <StyledLegalRail>
          <StyledTextNavLink
            to="/privacy"
            target="_blank"
            rel="noreferrer noopener"
            href="#"
          >
            <span>Privacy</span>
          </StyledTextNavLink>
          <StyledTextNavLink
            to="/terms"
            target="_blank"
            rel="noreferrer noopener"
            href="#"
          >
            <span>Terms of use</span>
          </StyledTextNavLink>
        </StyledLegalRail>
      </StyledSidebar>
      <StyledAccountBlock>
        <UserDropdown
          showLegal
          openInvitePopupCallback={() => {
            openInvitePopup();
          }}
          openSettingsCallback={() => {
            openSettingsPopup();
          }}
          logoutCallback={() => {
            if (isAuthenticated) {
              logout();
            }
          }}
          leaveGameCallback={() => {
            if (isAuthenticated) {
              leaveGame();
            }
          }}
          showTutorialCallback={() => {
            showTutorial();
          }}
        />
      </StyledAccountBlock>
    </StyledSideNav>
  );
});
